import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { getPlayers, getIndividualProgressWeeks, getIndividualProgress } from '../api';

// Same 6 lines as the live page, own colors picked for print contrast (the live page's
// 'overall' line is a light gray meant for a dark background — invisible on white paper).
const PRINT_METRICS = [
  { key: 'physical',     label: 'Physical',     color: '#0284c7' },
  { key: 'technical',    label: 'Technical',    color: '#16a34a' },
  { key: 'tactical',     label: 'Tactical',     color: '#9333ea' },
  { key: 'identity',     label: 'Identity',     color: '#d97706' },
  { key: 'availability', label: 'Availability', color: '#dc2626' },
  { key: 'overall',      label: 'Overall',      color: '#000000' },
];

const f0 = v => (v == null || isNaN(v) ? '—' : Math.round(v));

// Same dash-pattern-per-player convention as the live page's IndividualProgress.js, so the
// printed combined chart matches what the coach already saw on screen before exporting.
const PLAYER_DASH_PATTERNS = ['', '6 3', '2 3', '9 3 2 3', '1 3', '10 2 2 2'];

// ── Combined chart — every selected player on one graph, printed once above the per-player
// pages, mirroring the live page's CombinedChart. ────────────────────────────────────────────
function CombinedChartPage({ players, weeks, scoresByPlayer, hiddenLines }) {
  const chartData = weeks.map(w => {
    const row = { label: `W${w.weekNumber}` };
    players.forEach(p => {
      const score = (scoresByPlayer[p.id] || {})[w.scenarioId];
      PRINT_METRICS.forEach(m => { row[`p${p.id}_${m.key}`] = score?.[m.key] ?? null; });
    });
    return row;
  });
  const visibleMetrics = PRINT_METRICS.filter(m => !hiddenLines.includes(m.key));
  const CHART_W = 960, CHART_H = 320;

  return (
    <section className="player-page">
      <div style={{ paddingBottom: 10, marginBottom: 14, borderBottom: '2px solid #2563eb' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Individual Progress Report</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>All Selected Players</div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        {visibleMetrics.map(m => (
          <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: '#64748b' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, display: 'inline-block' }} />
            {m.label}
          </div>
        ))}
      </div>
      {players.length > 1 && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
          {players.map((p, i) => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 9, color: '#334155' }}>
              <svg width="20" height="8"><line x1="0" y1="4" x2="20" y2="4" stroke="#334155" strokeWidth="2" strokeDasharray={PLAYER_DASH_PATTERNS[i % PLAYER_DASH_PATTERNS.length]} /></svg>
              {p.name}
            </div>
          ))}
        </div>
      )}
      <LineChart width={CHART_W} height={CHART_H} data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 9, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
        <YAxis domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 9, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
        {players.map((p, pi) => visibleMetrics.map(m => (
          <Line key={`${p.id}_${m.key}`} dataKey={`p${p.id}_${m.key}`} name={`${p.name} ${m.label}`} stroke={m.color} strokeWidth={2}
            strokeDasharray={PLAYER_DASH_PATTERNS[pi % PLAYER_DASH_PATTERNS.length]}
            dot={{ r: 2.5, fill: m.color }} connectNulls={false} isAnimationActive={false} />
        )))}
      </LineChart>
    </section>
  );
}

// ── One player's page (table only now — the chart moved to the shared CombinedChartPage
// above, mirroring the live page's split into CombinedChart + PlayerTableSection) ────────────
function PlayerPage({ player, weeks, scoresByWeek }) {
  const CLR = { text: '#0f172a', muted: '#64748b', dimmed: '#94a3b8', border: '#e2e8f0', bg: '#f8fafc' };
  const TH = { padding: '5px 7px', fontSize: 8, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: CLR.muted, textAlign: 'center', borderBottom: `1px solid ${CLR.border}`, background: CLR.bg };
  const THL = { ...TH, textAlign: 'left' };
  const TD_ = { padding: '5px 7px', fontSize: 10, color: CLR.text, textAlign: 'center', borderBottom: `1px solid ${CLR.border}` };
  const TDL = { ...TD_, textAlign: 'left', fontWeight: 700 };
  const TDAVG = { ...TD_, fontWeight: 800, borderLeft: `1px solid ${CLR.border}`, borderRight: `1px solid ${CLR.border}`, background: CLR.bg };
  const avgOf = m => {
    const vals = weeks.map(w => scoresByWeek[w.scenarioId]?.[m.key]).filter(v => v != null && !isNaN(v));
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };

  return (
    <section className="player-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 10, marginBottom: 14, borderBottom: '2px solid #2563eb' }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: CLR.muted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Individual Progress Report</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: CLR.text, marginTop: 2 }}>{player?.name || '—'}</div>
          {player?.position && <div style={{ fontSize: 11, color: CLR.muted, marginTop: 2 }}>{player.position}</div>}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: CLR.text, marginBottom: 6 }}>Match History</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={THL}>Metric</th>
              <th style={{ ...TH, borderLeft: `1px solid ${CLR.border}`, borderRight: `1px solid ${CLR.border}` }}>Avg</th>
              {weeks.map(w => (
                <th key={w.scenarioId} style={TH}>
                  Week {w.weekNumber}
                  <div style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginTop: 1, color: CLR.dimmed }}>{w.opponentName || 'No match'}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PRINT_METRICS.map(m => (
              <tr key={m.key}>
                <td style={{ ...TDL, color: m.color }}>{m.label}</td>
                <td style={TDAVG}>{f0(avgOf(m))}</td>
                {weeks.map(w => <td key={w.scenarioId} style={TD_}>{f0(scoresByWeek[w.scenarioId]?.[m.key])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const PRINT_CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  @media print {
    @page { size: A4 landscape; margin: 12mm; }
    body  { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print { display: none !important; }
    section.player-page { page-break-after: always; break-after: page; }
    section.player-page:last-child { page-break-after: avoid; break-after: avoid; }
  }

  @media screen {
    body { background: #d1d5db; font-family: Inter, Arial, sans-serif; }
    .loading-screen {
      display: flex; align-items: center; justify-content: center;
      height: 100vh; font-family: Inter, Arial, sans-serif;
      font-size: 15px; color: #6b7280; gap: 12px;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    .spinner {
      width: 22px; height: 22px; border: 3px solid #e5e7eb;
      border-top-color: #2563eb; border-radius: 50%;
      animation: spin 0.75s linear infinite; flex-shrink: 0;
    }
    section.player-page {
      width: 297mm; min-height: 190mm;
      margin: 24px auto; background: white;
      box-shadow: 0 4px 24px rgba(0,0,0,0.18);
      padding: 14mm; box-sizing: border-box;
    }
  }
`;

// ── Main ──────────────────────────────────────────────────────────────────────
export default function IndividualProgressPrint() {
  const [pages, setPages] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [hiddenLines, setHiddenLines] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    let payload;
    try { payload = JSON.parse(localStorage.getItem('print_individual_progress') || 'null'); }
    catch { payload = null; }

    if (!payload || !payload.playerIds?.length || !payload.scenarioIds?.length) {
      setErrMsg('No players/weeks selected. Close this tab and try again.');
      setStatus('error');
      return;
    }
    setHiddenLines(payload.hiddenLines || []);

    Promise.all([getPlayers(), getIndividualProgressWeeks(), getIndividualProgress(payload.playerIds, payload.scenarioIds)])
      .then(([pl, allWeeks, scores]) => {
        const allPlayers = pl || [];
        const selectedPlayers = payload.playerIds
          .map(id => allPlayers.find(p => String(p.id) === String(id)))
          .filter(Boolean);
        const selectedWeeks = (allWeeks || [])
          .filter(w => payload.scenarioIds.includes(w.scenarioId))
          .sort((a, b) => a.weekNumber - b.weekNumber);

        const results = selectedPlayers.map(player => {
          const scoresByWeek = {};
          (scores || []).forEach(s => { if (s.player_id === player.id) scoresByWeek[s.scenario_id] = s; });
          return { player, scoresByWeek };
        });

        setWeeks(selectedWeeks);
        setPages(results);
        setStatus('ready');
      })
      .catch(e => { setErrMsg(e.message); setStatus('error'); });
  }, []);

  // Auto-trigger print dialog once all pages are ready — longer delay than MatchChronicPrint's
  // 700ms since these charts are fixed-size Recharts (not pure CSS), give layout more headroom.
  useEffect(() => {
    if (status !== 'ready') return;
    const t = setTimeout(() => window.print(), 1200);
    return () => clearTimeout(t);
  }, [status]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PRINT_CSS }} />

      {status === 'loading' && (
        <div className="loading-screen no-print">
          <div className="spinner" />
          Preparing player reports…
        </div>
      )}

      {status === 'error' && (
        <div className="loading-screen no-print" style={{ color: '#ef4444' }}>
          {errMsg}
        </div>
      )}

      {status === 'ready' && pages.length > 0 && (
        <CombinedChartPage
          players={pages.map(p => p.player)}
          weeks={weeks}
          scoresByPlayer={Object.fromEntries(pages.map(p => [p.player.id, p.scoresByWeek]))}
          hiddenLines={hiddenLines}
        />
      )}

      {status === 'ready' && pages.map(({ player, scoresByWeek }) => (
        <PlayerPage key={player.id} player={player} weeks={weeks} scoresByWeek={scoresByWeek} />
      ))}
    </>
  );
}
